import { useEffect, useRef, useState } from 'react';

const DEFAULT_BARS = 14;

/**
 * Waveform de grabación alimentada por el RMS REAL del mic (`getEnergy`),
 * calculada en un único `requestAnimationFrame` (copiada de speak):
 *
 *   - La FASE avanza con el tiempo → oleaje suave constante (idle).
 *   - La AMPLITUD se modula con la energía (peak-follower: ataque rápido,
 *     release lento), así idle vs hablando se distinguen claramente.
 */
export function LiveWaveform({
  getEnergy,
  barCount = DEFAULT_BARS,
}: {
  getEnergy: () => number;
  barCount?: number;
}) {
  const [heights, setHeights] = useState<number[]>(() =>
    Array.from({ length: barCount }, () => 0.1),
  );
  const phaseRef = useRef(0);
  const energyRef = useRef(0);

  // Envelope espacial: más alto al centro, suave en bordes (look clásico).
  const env = useRef<number[]>(
    Array.from({ length: barCount }, (_, i) => {
      const t = i / (barCount - 1);
      return 0.5 + 0.5 * Math.sin(t * Math.PI);
    }),
  );

  useEffect(() => {
    let raf = 0;
    let prev = 0;
    let lastEmit = 0;
    const tick = (now: number): void => {
      if (prev === 0) prev = now;
      const dt = Math.min(0.05, (now - prev) / 1000);
      prev = now;
      phaseRef.current += dt * 2.4;
      const raw = Math.max(0, Math.min(1, getEnergy()));
      const k = raw > energyRef.current ? Math.min(1, dt * 24) : Math.min(1, dt * 7);
      energyRef.current += (raw - energyRef.current) * k;

      if (now - lastEmit >= 33) {
        lastEmit = now;
        const ph = phaseRef.current;
        const e = energyRef.current;
        const dyn = Math.min(1, Math.sqrt(e) * 2.6);
        const amp = 0.12 + dyn * 0.86;
        const hs = new Array<number>(barCount);
        for (let i = 0; i < barCount; i += 1) {
          const x = i / (barCount - 1);
          const w1 = 0.5 + 0.5 * Math.sin(ph + x * Math.PI * 3);
          const w2 = 0.5 + 0.5 * Math.sin(ph * 0.6 + x * Math.PI * 5 + 1.3);
          const shape = w1 * 0.65 + w2 * 0.35;
          const h = amp * shape * (env.current[i] ?? 1);
          hs[i] = Math.max(0.06, Math.min(1, h));
        }
        setHeights(hs);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [getEnergy, barCount]);

  return (
    <div className="ptt-wave" aria-hidden="true">
      {heights.map((h, i) => (
        <span key={i} style={{ height: `${(h * 100).toFixed(1)}%` }} />
      ))}
    </div>
  );
}
